import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/cn";
import { RegionFlag } from "@/components/RegionFlag";

// `null` = All countries (brand-level aggregate, no market filter).
const ALL = "All";

export function RegionPicker({
  region,
  options,
  onChange,
}: {
  region: string | null;
  options: string[];
  onChange: (next: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-text-secondary bg-bg-subtle hover:bg-bg-hover rounded border border-border-lighter transition-colors w-full"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <RegionFlag region={region} />
        <span className="tracking-wide">{region ?? ALL}</span>
        <svg className="ml-auto w-3 h-3 text-text-dim" viewBox="0 0 12 12" aria-hidden>
          <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute z-20 left-0 right-0 mt-1 bg-bg-card border border-border-lighter rounded-md shadow-card py-1 max-h-64 overflow-y-auto"
        >
          {/* All countries — brand-level aggregate (default). */}
          <li key="__all__">
            <button
              role="option"
              aria-selected={region === null}
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left hover:bg-bg-hover",
                region === null ? "bg-accent-muted text-accent font-medium" : "text-text-secondary",
              )}
            >
              <RegionFlag region={null} />
              <span className="tracking-wide">{ALL}</span>
              <span className="ml-auto text-[10px] text-text-dim">all countries</span>
            </button>
          </li>
          {options.map((r) => (
            <li key={r}>
              <button
                role="option"
                aria-selected={r === region}
                onClick={() => {
                  onChange(r);
                  setOpen(false);
                }}
                className={cn(
                  "w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left hover:bg-bg-hover",
                  r === region ? "bg-accent-muted text-accent font-medium" : "text-text-secondary",
                )}
              >
                <RegionFlag region={r} />
                <span className="tracking-wide">{r}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
